import { useId, useState, type ReactNode } from 'react'
import type { APIToken, APITokenCreated, APITokenList } from '../api'
import { t, type Key } from '../i18n'
import { Button, Notice, ago } from '../components/ui'

/*
 * The pieces of Account → API tokens and Console → Settings → API tokens
 * (docs/api-tokens.md §8): the list, the create form and the panel that shows
 * a new token once.
 *
 * The secret lives only in the state of ShownOnce's parent while the panel is
 * open: it is never put into the query cache (the create is a plain request,
 * not a mutation, whose result the mutation cache would keep), the URL,
 * storage or the console. Closing the panel drops it.
 */

const DAY = 86_400_000
const date = (iso: string) => new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

export type Group = 'read' | 'write' | 'secrets'

/** Read / Write / Secrets: stream_keys:* are secrets, *:write writes, the rest reads. */
export function groupOf(scope: string): Group {
  if (scope.startsWith('stream_keys:')) return 'secrets'
  return scope.endsWith(':write') ? 'write' : 'read'
}

const scopeText = (s: string) => {
  const k = `apiTokens.scope.${s}` as Key
  const v = t(k)
  return v === undefined || v === k ? '' : v
}

export function expiryLabel(days: number): string {
  if (days === 0) return t('apiTokens.form.never')
  if (days === 365) return t('apiTokens.form.year')
  return t('apiTokens.form.days', { n: days })
}

/** "Never expires" (amber), "Expires in N days" (amber under 7), "Expired". */
function Expiry({ at, now }: { at: string | null; now: number }) {
  if (!at) return <span className="text-warn" data-testid="token-expiry" data-expiry="never">{t('apiTokens.row.never')}</span>
  const left = Date.parse(at) - now
  const days = Math.ceil(left / DAY)
  const text = left <= 0 ? t('apiTokens.row.expired') : days <= 1 && left < DAY ? t('apiTokens.row.expiresToday') : days === 1 ? t('apiTokens.row.expiresIn1') : t('apiTokens.row.expiresIn', { n: days })
  const soon = left < 7 * DAY
  return (
    <span className={soon ? 'text-warn' : undefined} title={date(at)} data-testid="token-expiry" data-expiry={left <= 0 ? 'expired' : soon ? 'soon' : 'later'}>
      {soon && '▲ '}
      {text}
    </span>
  )
}

export function ScopeChips({ scopes }: { scopes: string[] }) {
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label={t('apiTokens.form.scopes')}>
      {scopes.map((s) => (
        <li key={s} title={scopeText(s)} className={`rounded-sm border px-1.5 py-0.5 font-mono text-[11px] ${groupOf(s) === 'secrets' ? 'border-warn/60 text-warn' : 'border-line-2 text-text-soft'}`}>
          {s}
        </li>
      ))}
    </ul>
  )
}

/** One token: name, hint, scopes, created, expiry, last use, Revoke with an inline confirmation. */
export function TokenRow({ tk, onRevoke, owner, me }: { tk: APIToken; onRevoke: (id: number) => Promise<void>; owner?: boolean; me?: number }) {
  const [ask, setAsk] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const now = Date.now()
  const unused = (tk.last_used_at ? Date.parse(tk.last_used_at) : Date.parse(tk.created_at)) < now - 180 * DAY
  const revoke = async () => {
    setBusy(true)
    setErr('')
    try {
      await onRevoke(tk.id)
    } catch (e) {
      setErr(t('apiTokens.row.revokeFailed', { msg: (e as Error).message }))
      setBusy(false)
    }
  }
  return (
    <li
      data-testid="token-row"
      data-id={tk.id}
      className="flex flex-col gap-2.5 border-line py-4 max-md:rounded-[0.5rem] max-md:border max-md:bg-panel max-md:px-4 md:border-b md:last:border-b-0"
    >
      <div className="flex items-start justify-between gap-x-4">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="m-0 flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
            <span className="min-w-0 break-words font-medium text-text" data-testid="token-name">{tk.name}</span>
            <code className="font-mono text-xs text-muted" data-testid="token-hint">{tk.hint}</code>
          </p>
          {owner && tk.user_email && (
            <p className="m-0 text-xs text-text-soft" data-testid="token-owner">
              {t('apiTokens.row.owner', { email: tk.user_email })}
              {me !== undefined && tk.user_id === me && <span className="text-muted"> ({t('apiTokens.row.you')})</span>}
            </p>
          )}
        </div>
        {!ask && (
          <Button variant="danger" onClick={() => setAsk(true)} aria-label={`${t('apiTokens.row.revoke')}: ${tk.name}`} data-testid="token-revoke" className="flex-none max-md:min-h-[44px]">
            {t('apiTokens.row.revoke')}
          </Button>
        )}
      </div>
      <ScopeChips scopes={tk.scopes} />
      <p className="m-0 flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted">
        <span>{t('apiTokens.row.created', { date: date(tk.created_at) })}</span>
        <Expiry at={tk.expires_at} now={now} />
        <span data-testid="token-used">
          {tk.last_used_at ? [t('apiTokens.row.lastUsed', { when: ago(tk.last_used_at) }), tk.last_ip && t('apiTokens.row.from', { ip: tk.last_ip })].filter(Boolean).join(' ') : t('apiTokens.row.neverUsed')}
        </span>
        {unused && <span className="text-warn" data-testid="token-unused">{t('apiTokens.row.unused')}</span>}
      </p>
      {ask && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-fail/50 px-3 py-2" role="group" aria-label={t('apiTokens.row.revoke')} data-testid="token-revoke-confirm">
          <span className="text-sm text-text">{t('apiTokens.row.revokeAsk', { name: tk.name })}</span>
          <Button variant="danger" disabled={busy} onClick={() => void revoke()} data-testid="token-revoke-yes" className="max-md:min-h-[44px]">
            {busy ? t('apiTokens.row.revoking') : t('apiTokens.row.revoke')}
          </Button>
          <Button variant="quiet" disabled={busy} onClick={() => setAsk(false)} className="max-md:min-h-[44px]">
            {t('apiTokens.form.cancel')}
          </Button>
        </div>
      )}
      {err && <Notice>{err}</Notice>}
    </li>
  )
}

export function TokenList({ tokens, onRevoke, owner, me, testId = 'token-list' }: { tokens: APIToken[]; onRevoke: (id: number) => Promise<void>; owner?: boolean; me?: number; testId?: string }) {
  return (
    <ul data-testid={testId} className="m-0 flex list-none flex-col gap-3 p-0 md:gap-0 md:rounded-[0.5rem] md:border md:border-line md:bg-panel md:px-4">
      {tokens.map((tk) => (
        <TokenRow key={tk.id} tk={tk} onRevoke={onRevoke} owner={owner} me={me} />
      ))}
    </ul>
  )
}

const GROUPS: Group[] = ['read', 'write', 'secrets']
const GROUP_KEY: Record<Group, Key> = { read: 'apiTokens.group.read', write: 'apiTokens.group.write', secrets: 'apiTokens.group.secrets' }

export interface CreateInput {
  name: string
  scopes: string[]
  expires_in_days: number
}

/**
 * The create form: name (1–64), expiry from expiry_days (default_days first
 * selected), scopes in Read / Write / Secrets. Create stays off without a name
 * or a scope. `extra` is drawn above the buttons (the console's code note).
 * The create's own error shows under the form, with the server's text.
 */
export function CreateForm({ list, onCreate, onCancel, extra, quiet }: {
  list: APITokenList
  onCreate: (b: CreateInput) => Promise<void>
  onCancel: () => void
  extra?: ReactNode
  /** an error that needs no text (the code window was closed) */
  quiet?: (e: unknown) => boolean
}) {
  const id = useId()
  const [name, setName] = useState('')
  const [days, setDays] = useState(list.expiry_days.includes(list.default_days) ? list.default_days : list.expiry_days[0] ?? list.default_days)
  const [scopes, setScopes] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const trimmed = name.trim()
  const ok = trimmed.length >= 1 && [...trimmed].length <= 64 && scopes.length > 0
  const toggle = (s: string, on: boolean) => setScopes((x) => (on ? [...x.filter((y) => y !== s), s] : x.filter((y) => y !== s)))
  const submit = async () => {
    if (!ok || busy) return
    setBusy(true)
    setErr('')
    try {
      // the order of the server's list, not of the clicks
      await onCreate({ name: trimmed, scopes: list.scopes.filter((s) => scopes.includes(s)), expires_in_days: days })
    } catch (e) {
      if (!quiet?.(e)) setErr(t('apiTokens.form.error', { msg: (e as Error).message }))
      setBusy(false)
    }
  }
  const secrets = scopes.some((s) => groupOf(s) === 'secrets')
  return (
    <form
      data-testid="token-form"
      className="rs-corners flex flex-col gap-5 rounded-lg border border-line-2 bg-panel p-4"
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
    >
      <h2 className="m-0 text-lg font-semibold">{t('apiTokens.form.title')}</h2>
      <label htmlFor={`${id}-name`} className="flex max-w-md flex-col gap-1">
        <span className="text-sm font-medium">{t('apiTokens.form.name')}</span>
        <input
          id={`${id}-name`}
          data-testid="token-form-name"
          value={name}
          maxLength={64}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border border-line-2 bg-bg px-3 py-2 text-base text-text focus:border-accent focus:shadow-focus focus:outline-none max-md:min-h-[44px] sm:text-sm"
        />
        <span className="text-xs text-muted">{t('apiTokens.form.nameHint')}</span>
      </label>
      <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
        <legend className="mb-1.5 p-0 text-sm font-medium">{t('apiTokens.form.expiry')}</legend>
        <div role="radiogroup" aria-label={t('apiTokens.form.expiry')} className="flex flex-wrap gap-1.5" data-testid="token-form-expiry">
          {list.expiry_days.map((d) => (
            <button
              key={d}
              type="button"
              role="radio"
              aria-checked={days === d}
              data-days={d}
              onClick={() => setDays(d)}
              className={`min-h-9 rounded-md border px-3 text-sm max-md:min-h-[44px] ${days === d ? 'border-live/50 bg-live/[.12] text-live' : 'border-line-2 text-text-soft hover:text-text'}`}
            >
              {expiryLabel(d)}
            </button>
          ))}
        </div>
        {days === 0 && <span className="text-xs text-warn">▲ {t('apiTokens.form.neverHint')}</span>}
      </fieldset>
      <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
        <legend className="mb-1.5 p-0 text-sm font-medium">{t('apiTokens.form.scopes')}</legend>
        {GROUPS.map((g) => {
          const items = list.scopes.filter((s) => groupOf(s) === g)
          if (!items.length) return null
          return (
            <div key={g} className={`flex flex-col gap-1.5 rounded-md border p-3 ${g === 'secrets' ? 'border-warn/50' : 'border-line'}`} data-testid={`token-group-${g}`}>
              <span className={`font-mono text-[11px] font-semibold tracking-[.1em] uppercase ${g === 'secrets' ? 'text-warn' : 'text-muted'}`}>{t(GROUP_KEY[g])}</span>
              {g === 'secrets' && (
                <p className={`m-0 text-xs ${secrets ? 'text-warn' : 'text-text-soft'}`} data-testid="token-secrets-warn">
                  ▲ {t('apiTokens.secretsWarn')}
                </p>
              )}
              {items.map((s) => (
                <label key={s} className="flex min-h-9 cursor-pointer items-start gap-2.5 py-1 max-md:min-h-[44px]">
                  <input
                    type="checkbox"
                    data-scope={s}
                    checked={scopes.includes(s)}
                    onChange={(e) => toggle(s, e.target.checked)}
                    className="mt-0.5 h-4 w-4 flex-none accent-[var(--live)]"
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="font-mono text-xs text-text">{s}</span>
                    {scopeText(s) && <span className="text-xs text-muted">{scopeText(s)}</span>}
                  </span>
                </label>
              ))}
            </div>
          )
        })}
      </fieldset>
      {extra}
      {err && <Notice>{err}</Notice>}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="primary" type="submit" disabled={!ok || busy} data-testid="token-form-submit" className="max-md:min-h-[44px]">
          {busy ? t('apiTokens.form.creating') : t('apiTokens.form.submit')}
        </Button>
        <Button variant="quiet" type="button" onClick={onCancel} className="max-md:min-h-[44px]">
          {t('apiTokens.form.cancel')}
        </Button>
        {trimmed && !scopes.length && <span className="text-xs text-muted">{t('apiTokens.form.pickScope')}</span>}
      </div>
    </form>
  )
}

function CopyButton({ value, testId, onCopied }: { value: string; testId: string; onCopied?: () => void }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      data-testid={testId}
      className="flex-none max-md:min-h-[44px]"
      onClick={async () => {
        await navigator.clipboard?.writeText(value).catch(() => undefined)
        setCopied(true)
        onCopied?.()
        setTimeout(() => setCopied(false), 1500)
      }}
    >
      {copied ? t('apiTokens.once.copied') : t('apiTokens.once.copy')}
    </Button>
  )
}

/**
 * The new token, once: the secret with Copy, "You won't see it again" and a
 * curl line to try it. Done without a copy asks first. `base` is the name the
 * token works on, `path` a GET its scopes open.
 */
export function ShownOnce({ created, base, path, onClose }: { created: APITokenCreated; base: string; path: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  const [ask, setAsk] = useState(false)
  const curl = `curl -H "Authorization: Bearer ${created.token}" ${base}${path}`
  return (
    <section data-testid="token-once" aria-labelledby="token-once-title" className="rs-corners flex flex-col gap-4 rounded-lg border border-live/60 bg-panel p-4">
      <div className="flex flex-col gap-1">
        <h2 id="token-once-title" className="m-0 text-lg font-semibold">{t('apiTokens.once.title')}</h2>
        <p className="m-0 text-sm text-warn">▲ {t('apiTokens.once.warn')}</p>
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-xs text-muted">
          {t('apiTokens.once.token')} · <span className="text-text-soft">{created.api_token.name}</span>
        </span>
        <div className="flex items-start gap-2">
          <code aria-label={t('apiTokens.once.token')} data-testid="token-secret" className="min-w-0 flex-1 break-all rounded-md border border-line bg-bg px-3 py-2 font-mono text-sm text-text select-all">
            {created.token}
          </code>
          <CopyButton value={created.token} testId="token-copy" onCopied={() => setCopied(true)} />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-xs text-muted">{t('apiTokens.once.try')}</span>
        <div className="flex items-start gap-2">
          <code data-testid="token-curl" className="min-w-0 flex-1 break-all rounded-md border border-line bg-bg px-3 py-2 font-mono text-xs text-text-soft">
            {curl}
          </code>
          <CopyButton value={curl} testId="token-curl-copy" onCopied={() => setCopied(true)} />
        </div>
      </div>
      {ask ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-warn/50 px-3 py-2" role="group" data-testid="token-once-ask">
          <span className="text-sm text-text">{t('apiTokens.once.ask')}</span>
          <Button variant="danger" onClick={onClose} data-testid="token-once-close-anyway" className="max-md:min-h-[44px]">
            {t('apiTokens.once.closeAnyway')}
          </Button>
          <Button variant="quiet" onClick={() => setAsk(false)} className="max-md:min-h-[44px]">
            {t('apiTokens.once.back')}
          </Button>
        </div>
      ) : (
        <div>
          <Button variant="primary" data-testid="token-once-done" onClick={() => (copied ? onClose() : setAsk(true))} className="max-md:min-h-[44px]">
            {t('apiTokens.once.done')}
          </Button>
        </div>
      )}
    </section>
  )
}
